import { photos } from '@/data/universePhotosRequest';
import useResponsive from '@/hooks/useResponsive';
import clsx from 'clsx';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import { debounce } from 'lodash';
import router from 'next/router';
import { useEffect, useRef, useState } from 'react';

export default function Universe({ iframe }: { iframe?: boolean }) {
  const canvasWrapperRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(canvasWrapperRef, {
    amount: 0.5,
    once: true,
  });

  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    let resizeTimeout;

    const handleResize = debounce(() => {
      setIsResizing(true);
      resizeTimeout = setTimeout(() => setIsResizing(false), 300);
    }, 0);

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout); // Clear the timeout here
    };
  }, []);

  return (
    <motion.div
      initial={{ height: '100vh' }}
      animate={isInView && !iframe && { height: 'calc(100vh - 92px - 109px)' }}
      transition={{
        delay: 1.5,
      }}
      className={clsx([
        'transition-all ease-in-out duration-1000',
        !iframe && 'dts-fullwidth -mt-[112px]',
      ])}
    >
      <div
        className={clsx([
          'w-full overflow-hidden z-50 relative flex items-center justify-center',
          iframe ? 'h-screen' : 'h-[calc(100vh+109px)]',
        ])}
        ref={canvasWrapperRef}
      >
        {!iframe && (
          <div className="h-full w-full absolute z-10 -top-0 left-0 flex items-center justify-center pointer-events-none">
            <h1 className="text-center max-w-[1480px] mx-auto font-medium font-ace-bold leading-[1.1] uppercase text-[72px] 3xl:text-[80px] 4xl:text-[88px] -mt-[109px]">
              DECIPHER THE
              <br />
              VISUAL CHAOS
            </h1>
          </div>
        )}
        <AnimatePresence mode="wait">
          {isInView && !isResizing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={isInView && { opacity: 1, transition: { duration: 0.5, delay: 1.5 } }}
              exit={{ opacity: 0, transition: { duration: 0.2 } }}
            >
              <motion.div
                initial={{ scale: 1.1 }}
                animate={isInView && { scale: 1 }}
                transition={{
                  duration: 1,
                  delay: 2,
                  ease: [0.6, 0.01, -0.05, 0.9],
                }}
              >
                <InfiniteCanvas
                  iframe={iframe}
                  photos={photos}
                  dimensions={{
                    width: canvasWrapperRef.current?.offsetWidth || 0,
                    height: canvasWrapperRef.current?.offsetHeight || 0,
                  }}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

type UniverseElement = {
  x: number;
  y: number;
  width: number;
  height: number;
  image: HTMLImageElement;
  packId: string;
  id: string;
  isClickable: boolean;
};
type Point = { x: number; y: number };

function InfiniteCanvas({
  iframe,
  photos,
  dimensions,
}: {
  iframe?: boolean;
  photos;
  dimensions: { width: number; height: number };
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const elementsRef = useRef<UniverseElement[]>([]);
  const offsetRef = useRef<Point>({
    x: dimensions.width * 2,
    y: dimensions.height * 3,
  });
  const mouseRef = useRef<Point>({
    x: dimensions.width * 2,
    y: dimensions.height * 3,
  });
  const targetRef = useRef<Point>({
    x: dimensions.width * 2,
    y: dimensions.height * 3,
  });
  const { isMobile } = useResponsive();

  const canvasScale = 2;
  const speedRef = useRef<number>(0.001);

  const clickableImage = useRef<UniverseElement | null>(null);

  useEffect(() => {
    const elements = elementsRef.current;
    const offset = offsetRef.current;
    const mouse = mouseRef.current;
    const cursorTarget = targetRef.current;
    const cursorSpeed = 0.075;
    const canvas = canvasRef.current!; // shouldn't enforce this but we'll let it go for timesake
    const ctx = canvas?.getContext('2d');
    let animationFrameID: number;

    // Set the canvas dimensions to the window dimensions
    canvas.width = dimensions.width * canvasScale;
    //check if the screen height is > 1000px or if the user is on mobile
    const screenHeight = window.innerHeight;
    if (screenHeight > 1000 || (isMobile && screenHeight > 500)) {
      canvas.height = dimensions.height * canvasScale;
    } else if (isMobile) {
      canvas.height = 500 * canvasScale;
    } else {
      canvas.height = 1000 * canvasScale;
    }

    //create an array of 48 random images from the photos array
    const randomPhotos = photos
      .sort(() => Math.random() - Math.random())
      .slice(0, isMobile ? 24 : 48);

    randomPhotos.forEach((photo, i) => {
      const image = new Image();
      image.src = isMobile
        ? photo.imgix.links.small_image_url
        : photo.imgix.links.large_image_url || '';
      // Set up the onerror event handler to handle loading failures
      image.onerror = () => console.error(`Failed to load image: ${photo.large_image_url}`);

      const { x, y } = newImageLocation(canvas, i, isMobile);

      image.onload = () => {
        const { height, width } = generateImageDimensions(image, isMobile);

        // Randomly determine the vertical and horizontal position adjustments
        const verticalOffset = Math.random() * height - height / 4;
        const horizontalOffset = Math.random() * width - width / 4;

        const adjustedY = y + verticalOffset;
        const adjustedX = x + horizontalOffset;

        elements.push({
          x: adjustedX,
          y: adjustedY,
          image,
          width,
          height,
          packId: photo.pack_id,
          id: photo.id,
          isClickable: false,
        });
      };
    });

    animate();
    function animate() {
      // Update the scroll position based on the distance vector
      offset.x = offset.x + (mouse.x - canvas.width / 2) * speedRef.current;
      offset.y = offset.y + (mouse.y - canvas.height / 2) * speedRef.current;
      // get new element positions and sizes

      const updatedElements = animateElements(
        elements,
        mouse,
        cursorTarget,
        offset,
        canvas,
        photos,
        isMobile
      );
      // set the clickable image to the first element that is clickable
      const clickable =
        updatedElements
          .slice()
          .reverse()
          .find((el) => el.isClickable) || null;

      if (!iframe) {
        clickableImage.current = clickable;
        clickable ? (canvas.style.cursor = 'pointer') : (canvas.style.cursor = 'default');
      }

      if (!ctx) return;

      // clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // iterate through each element in the array
      updatedElements.forEach((element) => {
        // draw the element
        ctx.drawImage(element.image, element.x, element.y, element.width, element.height);

        // check if the element is the clickable image
        if (element === clickable && !iframe) {
          // draw the border around the clickable image
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 2;
          ctx.strokeRect(element.x, element.y, element.width, element.height);
        }
      });
      animationFrameID = requestAnimationFrame(animate);
    }

    // This function updates the target position
    function handleMouseMove(event) {
      const rect = canvas.getBoundingClientRect();
      cursorTarget.x = event.clientX - rect.left;
      cursorTarget.y = event.clientY - rect.top;
    }

    // This function updates the custom cursor's position
    function updateMousePosition() {
      mouse.x += (cursorTarget.x - mouse.x) * cursorSpeed;
      mouse.y += (cursorTarget.y - mouse.y) * cursorSpeed;

      // Here you can draw or update your custom cursor using mouse.x and mouse.y

      requestAnimationFrame(updateMousePosition);
    }

    updateMousePosition();

    canvasRef.current?.addEventListener('mousemove', handleMouseMove);
    return () => {
      canvasRef.current?.removeEventListener('mousemove', handleMouseMove);
      elementsRef.current = [];
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
      cancelAnimationFrame(animationFrameID); // Cancel the animation frame using the stored ID
    };
  }, [photos]);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  function handleMouseHover(direction: 'up' | 'down') {
    clearTimeout(timeoutRef.current!);

    if (direction === 'up') {
      animateSpeed(0.008);
    } else if (direction === 'down') {
      animateSpeed(0.001);
    }
  }

  function animateSpeed(targetSpeed: number) {
    let currentSpeed = speedRef.current;

    function updateSpeed() {
      const step = (targetSpeed - currentSpeed) / 40; // Recalculate the step value
      currentSpeed += step;
      speedRef.current = currentSpeed;

      if ((step > 0 && currentSpeed < targetSpeed) || (step < 0 && currentSpeed > targetSpeed)) {
        timeoutRef.current = setTimeout(updateSpeed, 10) as unknown as NodeJS.Timeout;
      } else {
        clearTimeout(timeoutRef.current!);
      }
    }

    updateSpeed();
  }

  return (
    <canvas
      ref={canvasRef}
      onClick={() =>
        clickableImage?.current?.isClickable && router.push(`/photo/${clickableImage?.current?.id}`)
      }
      onMouseLeave={() => handleMouseHover('down')}
      onMouseEnter={() => handleMouseHover('up')}
      className={clsx('absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2')}
    />
  );
}

/**
 * This can be our placement function
 * @param canvas - the canvas element
 * @param i - index of the image
 * @param canvasScale - scale of the canvas
 * @returns
 */
function newImageLocation(canvas: HTMLCanvasElement, i: number, isMobile: boolean) {
  // Divide the canvas into a grid of 8 columns and 6 rows for desktop, or 12 rows and 4 columns for mobile
  const gridWidth = isMobile ? canvas.width / 4 : canvas.width / 8;
  const gridHeight = isMobile ? canvas.height / 6 : canvas.height / 6;

  // Place the image in the grid based on the index
  const columns = isMobile ? 4 : 8;
  const gridX = (i % columns) * gridWidth;
  const gridY = Math.floor(i / columns) * gridHeight;

  // Return the x and y coordinates
  return {
    x: gridX,
    y: gridY,
  };
}

/**
 * Returns the width and height of the image based on the aspect ratio
 * @param image
 */
function generateImageDimensions(image: HTMLImageElement, isMobile: boolean) {
  // generate a number between 0 and 1 and use this to create a bell curve distribution of heights
  const random = Math.random();
  const bellCurveValue = random < 0.2 ? 0.25 : random < 0.6 ? 0.35 : random < 0.8 ? 0.4 : 0.45;

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  if (isMobile) {
    const widthVW = Math.floor(viewportWidth * bellCurveValue);
    const aspectRatio = image.width / image.height;
    const heightVH = Math.floor(widthVW / aspectRatio);

    return { height: heightVH, width: widthVW };
  }

  const heightVH = Math.floor(viewportHeight * bellCurveValue);
  const widthVW = (heightVH * image.width) / image.height;

  return { height: heightVH, width: widthVW };
}

/**
 * Returns a multiplier to scale the element based on the distance from the mouse
 * @param distFromMouse
 * @param startDistance
 * @param endDistance
 * @param maxScale
 * @returns
 */
function scaleMultiplier(
  distFromMouse: number,
  startDistance: number,
  endDistance: number,
  maxScale: number
) {
  const score = easeInOutSine(
    Math.min(1, Math.max(0, 1 - (distFromMouse - endDistance) / (startDistance - endDistance)))
  );
  return 1 + score * maxScale;
}

/**
 * Easing Functions
 * @param x
 * @returns
 */
function easeInOutSine(x: number) {
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

/**
 *  Returns the distance between two points
 * @param a
 * @param b
 * @returns
 */
function distanceBetweenPoints(a: Point, b: Point) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Takes the elements and returns an array of elements with their positions and sizes updated
 * This should provide the motion effects for interaction with the mouse position
 * @param elements
 * @param mouse
 * @param offset
 * @param elementSize
 * @returns
 */
function animateElements(
  elements: UniverseElement[],
  mouse: Point,
  cursorTarget: Point,
  offset: Point,
  canvas: HTMLCanvasElement,
  photos,
  isMobile
) {
  return elements
    .map((element) => {
      const relativeElement = {
        x: (((element.x - offset.x + canvas.width) % canvas.width) + canvas.width) % canvas.width,
        y:
          (((element.y - offset.y + canvas.height) % canvas.height) + canvas.height) %
          canvas.height,
      };

      //Generate new image if element is within 3px of left edge of canvas
      //Images that leave from the right side will also be replaced when they are redrawn on the left side
      if (relativeElement.x <= 3) {
        const i = Math.floor(Math.random() * photos.length);
        // pick a new random image for the element
        const image = new Image();
        //generate a random number between 0 and the length of the photos array
        image.src = isMobile
          ? photos[i].imgix.links.small_image_url
          : photos[i].imgix.links.large_image_url || '';
        // Set up the onerror event handler to handle loading failures
        image.onerror = () => console.error(`Failed to load image: ${photos[i].large_image_url}`);

        image.onload = () => {
          const { height, width } = generateImageDimensions(image, isMobile);
          element.width = width;
          element.height = height;
          element.image = image;
        };
      }

      const distFromMouse = distanceBetweenPoints(mouse, relativeElement);
      return { element, distFromMouse };
    })
    .sort((a, b) => {
      // sort by closest last so they are drawn on top
      return b.distFromMouse - a.distFromMouse;
    })
    .map(({ element, distFromMouse }, i) => {
      // scale based on proximity to mouse
      const distMultiplier = scaleMultiplier(distFromMouse, 600, 0, 0.2);

      // Calculate the width and height of the element based on the aspect ratio not the elementSize
      const elementWidth = element.width * distMultiplier;
      const elementHeight = element.height * distMultiplier;

      // Calculate the element's position relative to the canvas
      const adjustedX = element.x - offset.x - elementWidth / 2;
      const adjustedY = element.y - offset.y - elementHeight / 2;

      return {
        ...element,
        x: adjustedX,
        y: adjustedY,
        width: elementWidth,
        height: elementHeight,
        distMultiplier,
      };
    })
    .map((element) => {
      const modX = element.x % canvas.width;
      const modY = element.y % canvas.height;
      element.x = modX > 0 ? modX : canvas.width + modX || element.x;
      element.y = modY > 0 ? modY : canvas.height + modY || element.y;

      const elementCenter = {
        x: element.x + element.width / 2,
        y: element.y + element.height / 2,
      };
      const canvasCenter = {
        x: canvas.width / 2,
        y: canvas.height / 2,
      };

      const pxToPush = (element.distMultiplier - 1) * 0.75;
      // If the element is scaled up, push it away from the center
      const pushX = (elementCenter.x - canvasCenter.x) * pxToPush;
      const pushY = (elementCenter.y - canvasCenter.y) * pxToPush;

      //determine if element is clickable based on if the mouse is within the element
      const isClickable =
        cursorTarget.x > element.x + pushX &&
        cursorTarget.x < element.x + element.width + pushX &&
        cursorTarget.y > element.y + pushY &&
        cursorTarget.y < element.y + element.height + pushY;

      return { ...element, x: element.x + pushX, y: element.y + pushY, isClickable };
    });
}
